import { useEffect, useState, useCallback } from 'react';
import { toast } from 'sonner';
import { Package, Download, ShieldCheck, FileArchive, Loader2, Lock } from 'lucide-react';
import api from '../lib/api';
import { formatDateTime } from '../lib/constants';
import MissionShell from '../components/MissionShell';
import { MISSIONS } from '../lib/missions';

function humanBytes(n) {
  if (!n) return '—';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(2)} MB`;
}

export default function Fiscalizacion() {
  const [history, setHistory] = useState([]);
  const [generating, setGenerating] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/fiscalizacion/history');
      setHistory(data);
    } catch (e) {
      if (e?.response?.status === 403) toast.error('Solo el DPO (admin o compliance officer) puede acceder aquí');
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const generate = async () => {
    setGenerating(true);
    try {
      const res = await api.post('/fiscalizacion/export', {}, { responseType: 'blob' });
      const hash = res.headers?.['x-priviax-sha256'];
      // Nombre del archivo desde Content-Disposition si viene
      const cd = res.headers?.['content-disposition'] || '';
      const m = cd.match(/filename="?([^";]+)"?/i);
      const filename = m ? m[1] : `Priviax_Fiscalizacion_${new Date().toISOString().slice(0, 10)}.zip`;

      const blob = new Blob([res.data], { type: 'application/zip' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = filename;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success('Carpeta generada · descarga iniciada');
      if (hash) {
        console.log('SHA-256:', hash);
      }
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Error al generar la carpeta');
    } finally { setGenerating(false); }
  };

  const last = history[0];

  return (
    <MissionShell
      mission={MISSIONS.fiscalizacion}
      idPrefix="fisc-mission"
      doHint={history.length === 0
        ? 'Siguiente acción: genera tu primera carpeta para ver qué evidencia tienes hoy y qué falta completar.'
        : 'Genera una nueva carpeta cada vez que completes un paso importante de tu ruta, así mantienes un historial de tu avance.'}
      evidence={[
        { label: 'Carpetas generadas', value: history.length, tone: history.length ? 'ok' : 'warn' },
        { label: 'Última generación', value: last ? formatDateTime(last.exported_at) : '—', tone: 'ok', detail: last?.exported_by_email },
        { label: 'Huella SHA-256 (última)', value: last?.zip_sha256 ? `${last.zip_sha256.slice(0, 12)}…` : '—', tone: 'ok', detail: last ? `${last.file_count} archivos` : undefined },
      ]}
      evidenceNote="El historial de carpetas generadas queda registrado y no puede modificarse."
    >
    <div className="max-w-5xl space-y-6" data-testid="fiscalizacion-page">
      {/* Impact card */}
      <div className="grid grid-cols-1 md:grid-cols-[1fr_auto] gap-0 border-2 border-[#143A34]">
        <div className="p-5 bg-[#0F2A26] text-white">
          <div className="flex items-center gap-2 mb-1">
            <Package size={16} />
            <span className="text-[10px] uppercase tracking-[0.14em] font-semibold text-white/85">Carpeta de fiscalización</span>
          </div>
          <div className="text-[22px] font-semibold leading-tight tracking-tight">
            Un clic ante la Agencia
          </div>
          <div className="text-[12.5px] text-white/85 mt-1.5 max-w-xl">
            Genera un ZIP con toda la evidencia de cumplimiento (RAT, documentos, sistemas,
            bitácora, brechas, DPIA, ARCO+ y CMP), cada archivo con su hash SHA-256.
            Listo para entregar a la Agencia de Protección de Datos.
          </div>
          <div className="flex flex-wrap items-center gap-3 mt-3 text-[11px] text-white/80">
            <span className="inline-flex items-center gap-1"><ShieldCheck size={11} /> Hash SHA-256 por archivo</span>
            <span className="inline-flex items-center gap-1"><FileArchive size={11} /> ZIP verificable</span>
            <span className="inline-flex items-center gap-1"><Lock size={11} /> Solo DPO o admin</span>
          </div>
        </div>
        <div className="p-5 flex items-center justify-center bg-white">
          <button
            onClick={generate}
            disabled={generating}
            className="px-5 py-3 bg-[#143A34] hover:bg-[#0F2A26] text-white text-[13px] font-medium flex items-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed"
            data-testid="generate-export">
            {generating ? <><Loader2 size={14} className="animate-spin" /> Generando…</> : <><Download size={14} /> Generar y descargar ZIP</>}
          </button>
        </div>
      </div>

      {/* Historial */}
      <section>
        <h2 className="text-[13px] font-semibold text-[#1C2329] mb-2">Exportaciones anteriores</h2>
        {loading ? (
          <div className="text-[12.5px] text-[#6B7280]">Cargando…</div>
        ) : history.length === 0 ? (
          <div className="border border-[#E5E7EB] bg-white p-8 text-center">
            <FileArchive size={24} className="mx-auto text-[#9CA3AF]" strokeWidth={1.5} />
            <div className="text-[13px] font-medium text-[#1C2329] mt-2">Aún no ha generado ninguna carpeta</div>
            <div className="text-[11.5px] text-[#6B7280] mt-1">Cada exportación queda registrada aquí con su hash SHA-256 como evidencia.</div>
          </div>
        ) : (
          <div className="border border-[#E5E7EB] bg-white overflow-x-auto">
            <table className="pv-table" data-testid="fiscalizacion-history">
              <thead><tr>
                <th style={{ width: 160 }}>Fecha</th>
                <th>Generado por</th>
                <th style={{ width: 100 }}>Tamaño</th>
                <th style={{ width: 100 }}>Archivos</th>
                <th>SHA-256</th>
              </tr></thead>
              <tbody>
                {history.map((h) => (
                  <tr key={h.id} data-testid={`export-${h.id}`}>
                    <td className="text-[12px] text-[#1C2329]">{formatDateTime(h.exported_at)}</td>
                    <td className="text-[12px]">{h.exported_by_email}</td>
                    <td className="text-[12px]">{humanBytes(h.zip_size_bytes)}</td>
                    <td className="text-[12px]">{h.file_count}</td>
                    <td className="text-[10.5px] font-mono text-[#6B7280] truncate max-w-xs" title={h.zip_sha256}>{h.zip_sha256?.slice(0, 20)}…</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
    </MissionShell>
  );
}
